import { activeCallAtom, callDockAnchorAtom, callStore } from '@/features/interview-scheduling/call-store'
import { DigitalSambaRoom } from '@/features/interview-scheduling/digital-samba-room'
import { useNavigate } from '@tanstack/react-router'
import { useAtom, useAtomValue } from 'jotai'
import { Maximize2Icon, PhoneOffIcon } from 'lucide-react'
import { useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

/**
 * The app-wide Jobilla Meet call, mounted once at the router root so it keeps running across
 * tab switches, candidate changes, and full page navigations. When the candidate's Jobilla Meet
 * tab is open it docks over that tab (tracking its box); otherwise it shrinks to a video-only
 * popout pinned to the bottom-right of the viewport. The call element never changes DOM parent,
 * so the Digital Samba iframe survives every transition instead of reloading.
 */
export function GlobalCallDock() {
	const { t } = useTranslation('common')
	const navigate = useNavigate()
	const [activeCall, setActiveCall] = useAtom(activeCallAtom, { store: callStore })
	const anchor = useAtomValue(callDockAnchorAtom, { store: callStore })
	const [rect, setRect] = useState<DOMRect | null>(null)

	// While docked, follow the tab's box as the panel resizes / the page scrolls. Measuring in a
	// layout effect (before paint) avoids a one-frame popout flash when the tab first opens.
	useLayoutEffect(() => {
		if (anchor === null) {
			setRect(null)

			return
		}
		const measure = () => setRect(anchor.getBoundingClientRect())
		measure()
		const observer = new ResizeObserver(measure)
		observer.observe(anchor)
		window.addEventListener('resize', measure)
		window.addEventListener('scroll', measure, true)

		return () => {
			observer.disconnect()
			window.removeEventListener('resize', measure)
			window.removeEventListener('scroll', measure, true)
		}
	}, [anchor])

	if (activeCall === null) {
		return null
	}

	const docked = anchor !== null && rect !== null

	function handleExpand() {
		if (activeCall === null) {
			return
		}
		void navigate({
			to: '/company/$companyId/$campaignId/candidates/$candidateId',
			params: {
				companyId: activeCall.companyId,
				campaignId: activeCall.campaignId,
				candidateId: activeCall.candidateId,
			},
			search: { tab: 'meeting' },
		})
	}

	// Portalled to <body> and above the candidate slide-over (a Headless UI Dialog at z-50) so the
	// call is never trapped behind it. The call UI lives in the DS iframe, whose clicks don't leak
	// to the parent document, so interacting with it won't trip the slide-over's outside-click close.
	return createPortal(
		<div
			className={docked
				? 'fixed z-[60]'
				: 'fixed bottom-4 right-4 z-[60] h-44 w-72 overflow-hidden rounded-xl border border-brand-900/40 bg-brand-2000 shadow-2xl'}
			style={docked && rect !== null
				? { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
				: undefined}
		>
			<DigitalSambaRoom joinUrl={activeCall.joinUrl} onLeave={() => setActiveCall(null)} minimal={!docked} />
			{docked
				? null
				: (
					<div className="absolute right-2 top-2 z-10 flex gap-1.5">
						<button
							type="button"
							onClick={handleExpand}
							aria-label={t('interviews.videoCall.expand')}
							title={t('interviews.videoCall.expand')}
							className="inline-flex cursor-pointer items-center justify-center rounded-md bg-brand-2000/70 p-1.5 text-white/90 backdrop-blur transition-colors hover:bg-brand-2000"
						>
							<Maximize2Icon className="size-4" />
						</button>
						<button
							type="button"
							onClick={() => setActiveCall(null)}
							aria-label={t('interviews.videoCall.leave')}
							title={t('interviews.videoCall.leave')}
							className="inline-flex cursor-pointer items-center justify-center rounded-md bg-red-600/85 p-1.5 text-white transition-colors hover:bg-red-600"
						>
							<PhoneOffIcon className="size-4" />
						</button>
					</div>
				)}
		</div>,
		document.body,
	)
}
